// Single gatekeeper for the persisted logged-in user.
//
// Why this exists: the whole app reads the user back with a bare
// `JSON.parse(user)` (App.js, Header, every dashboard/panel). That parse throws
// on ANY value that is not valid JSON, and a throw during App's render unmounts
// the entire React tree — a permanent white screen that survives reloads,
// because the bad value lives in localStorage.
//
// The value that got in was the literal string "undefined":
// `JSON.stringify(undefined)` returns the VALUE undefined (not a string), and
// `localStorage.setItem(k, undefined)` coerces it to "undefined". So any login /
// profile-refresh response whose expected field was missing wrote a permanent
// app-breaking value — silently, with no error at write time.
//
// The invariant restored here: `localStorage["User"]` and the redux mirror
// `Authentication.user` are ALWAYS either a valid JSON string or "" — never
// "undefined", never "null", never "[object Object]". Every existing
// `JSON.parse(user)` site therefore keeps working unchanged.

import {
  readJson,
  safeJsonParse,
  safeJsonStringify,
  writeJson,
} from "./safeJson";

export const USER_STORAGE_KEY = "User";

// Values that are technically parseable or storable but carry no user, and
// which the raw-`JSON.parse` call sites cannot survive.
const isBlank = (raw) =>
  raw === null ||
  raw === undefined ||
  raw === "" ||
  raw === "undefined" ||
  raw === "null";

/**
 * Parse a stored/redux user into an object. NEVER throws and never returns a
 * primitive, so `const { _id } = parseUser(x)` is always safe — several call
 * sites destructure the result directly.
 */
export const parseUser = (raw) => {
  if (isBlank(raw)) return {};
  if (typeof raw === "object") return raw; // already an object — nothing to parse
  if (typeof raw !== "string") return {};
  const parsed = safeJsonParse(raw, null);
  return parsed && typeof parsed === "object" ? parsed : {};
};

/**
 * Normalize anything a caller wants to treat as "the user" into the JSON-string
 * form the reducer and the JSON.parse call sites expect. Unusable input becomes
 * "" rather than a poisoned string.
 */
export const toUserString = (value) => {
  if (isBlank(value)) return "";
  // A string is only kept if it actually parses back into a user object — this
  // is what rejects "undefined" / "[object Object]" / truncated JSON.
  if (typeof value === "string") {
    return Object.keys(parseUser(value)).length ? value : "";
  }
  if (typeof value !== "object") return "";
  const serialized = JSON.stringify(value);
  return typeof serialized === "string" && serialized !== "{}" ? serialized : "";
};

/**
 * The ONLY supported way to persist the user. Given a user object (or an
 * already-serialized user) it stores valid JSON; given anything unusable —
 * undefined, null, a missing API field — it REMOVES the key instead of writing
 * a value that would break the next render.
 */
export const setStoredUser = (value) => {
  const serialized = toUserString(value);
  if (!serialized) {
    // Loud on purpose. A refused write means an API response did not carry the
    // user where the caller expected it (`res.data.user` vs `res.data.data`,
    // an error branch, a shape change). That used to corrupt storage silently
    // and only surface later as a blank page; now it costs the session at most,
    // and leaves a breadcrumb naming the real problem. console.warn survives the
    // production console.log stub in index.js.
    console.warn(
      "[storedUser] refusing to persist an unusable user value:",
      value,
    );
    localStorage.removeItem(USER_STORAGE_KEY);
    return "";
  }
  localStorage.setItem(USER_STORAGE_KEY, serialized);
  return serialized;
};

/**
 * Read the stored user as a JSON string, dropping a corrupt value on the way.
 * Called while the auth reducer builds its initial state — i.e. before anything
 * renders — so a browser already holding "undefined" from an older build heals
 * itself on the next load instead of white-screening until site data is cleared.
 */
export const readStoredUser = () => {
  let raw = null;
  try {
    raw = localStorage.getItem(USER_STORAGE_KEY);
  } catch {
    return ""; // storage blocked (private mode, hardened settings)
  }
  if (raw === null) return "";
  const serialized = toUserString(raw);
  if (!serialized) {
    // Corrupt leftover ("undefined", "null", "", truncated JSON). Drop the whole
    // session, not just this key: the ~18 components that still read
    // localStorage["User"] and JSON.parse it directly need it gone to stop
    // throwing, and a token WITHOUT a user is an unusable half-session — the
    // private routes derive `role` from the user, so keeping the token would
    // bounce the app between role-less dashboard redirects. Clearing both lands
    // the user on the normal logged-out entry point, where reconnecting the
    // wallet rebuilds everything. This is what heals a browser already stuck on
    // the white screen, with no "clear site data" needed.
    try {
      localStorage.removeItem(USER_STORAGE_KEY);
      localStorage.removeItem("Token");
      localStorage.setItem("isLogout", true);
    } catch {
      /* nothing more we can do */
    }
    return "";
  }
  return serialized;
};

// ---------------------------------------------------------------------------
// The wallet blob, persisted alongside the user and carrying the SAME bug: every
// site wrote `JSON.stringify(response?.data?.data?.userWallet)`, which is the
// value undefined whenever that field is missing. Header reads it back and
// parses it inside an effect — and React takes the tree down for an uncaught
// effect error exactly as it does for a render error.
// ---------------------------------------------------------------------------

export const WALLET_STORAGE_KEY = "User Wallet";

/** Never throws; always safe to property-access. */
export const parseWallet = (raw) => {
  const parsed = safeJsonParse(raw, null);
  return parsed && typeof parsed === "object" ? parsed : {};
};

/**
 * Normalize anything into the JSON-string form the reducer and Header expect.
 * Unlike the user, an EMPTY wallet is legitimate — `signIn` returns
 * `userWallet: []` — so "[]" and "{}" are kept and only unusable input is
 * rejected.
 */
export const toWalletString = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = typeof value === "string" ? safeJsonParse(value, null) : value;
  if (parsed === null || typeof parsed !== "object") return "";
  return safeJsonStringify(parsed);
};

/** The only supported way to persist the wallet blob. */
export const setStoredWallet = (value) => writeJson(WALLET_STORAGE_KEY, value);

/** Sanitized read used to seed the reducer, dropping a corrupt entry. */
export const readStoredWallet = () => toWalletString(readJson(WALLET_STORAGE_KEY, null));

/**
 * Sanitize and read the whole persisted session in ONE step.
 *
 * Ordering matters and must not be left to the order of keys in an object
 * literal: dropping a corrupt user also clears the token, so a reducer that read
 * `token` first captured a token whose user no longer existed. That produced a
 * role-less half-session — and the private routes then redirected to themselves
 * forever (React error #185). Reading everything here, after the cleanup, makes
 * that impossible to get wrong at the call site.
 */
export const readSession = () => {
  const user = readStoredUser(); // clears Token too if the user is corrupt
  const wallet = readStoredWallet();
  let token = "";
  try {
    token = localStorage.getItem("Token") || "";
  } catch {
    token = "";
  }
  // Belt and braces: a token without a user cannot route anywhere.
  return { user, wallet, token: user ? token : "" };
};
